import { Component, computed, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ANY_AVAILABLE_PROVIDER, formatMinutesDuration } from '../../util';

export interface CartItem {
  name: string;
  duration: number; // in minutes
  imageUrl?: string;
  providerName?: string;
  initials?: string;
}

@Component({
  selector: 'app-booking-cart',
  standalone: false,
  templateUrl: './booking-cart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./booking-cart.component.scss'],
})
export class BookingCartComponent {
  private readonly translate = inject(TranslateService);

  private readonly defaultCtaLabel = toSignal(this.translate.stream('BOOKING_CART.CHOOSE_PROVIDER'), {
    initialValue: 'Choose provider',
  });
  private readonly defaultBackLabel = toSignal(this.translate.stream('BOOK.BACK'), { initialValue: 'Back' });
  private readonly defaultHeaderTitle = toSignal(this.translate.stream('BOOKING_CART.YOUR_SELECTION'), {
    initialValue: 'Your selection',
  });

  items = input<CartItem[]>([]);
  ctaLabel = input<string>();
  effectiveCtaLabel = computed(() => this.ctaLabel() ?? this.defaultCtaLabel());
  mobileCtaLabel = input<string>();
  ctaDisabled = input(false);
  showBackButton = input(false);
  backLabel = input<string>();
  effectiveBackLabel = computed(() => this.backLabel() ?? this.defaultBackLabel());
  headerTitle = input<string>();
  effectiveHeaderTitle = computed(() => this.headerTitle() ?? this.defaultHeaderTitle());
  scheduledDate = input<string>();
  scheduledTime = input<string>();
  showMakeChangesLink = input(false);
  warningMessage = input<string>();
  readOnly = input(false);

  ctaClick = output<void>();
  backClick = output<void>();
  removeItem = output<number>();
  makeChangesClick = output<void>();

  mobileExpanded = signal(false);

  totalDurationMinutes = computed(() =>
    this.items().reduce((sum, item) => sum + item.duration, 0)
  );

  totalHours = computed(() => Math.floor(this.totalDurationMinutes() / 60));

  totalMinutes = computed(() => this.totalDurationMinutes() % 60);

  totalDurationLabel = computed(() => formatMinutesDuration(this.totalDurationMinutes()));

  private readonly anyAvailableLabel = toSignal(this.translate.stream('PROVIDER_SELECTION.ANY_AVAILABLE'), {
    initialValue: ANY_AVAILABLE_PROVIDER,
  });

  durationLabel(minutes: number): string {
    return formatMinutesDuration(minutes);
  }

  providerLabel(item: CartItem): string {
    return item.providerName === ANY_AVAILABLE_PROVIDER ? this.anyAvailableLabel() : (item.providerName ?? '');
  }

  isEmpty = computed(() => this.items().length === 0);

  toggleMobileExpand(): void {
    this.mobileExpanded.update((v) => !v);
  }

  onCtaClick(): void {
    this.ctaClick.emit();
  }

  onBackClick(): void {
    this.backClick.emit();
  }

  onRemoveItem(index: number): void {
    this.removeItem.emit(index);
  }
}
